'use client';
import React, { useState } from 'react';
import AppLayout from '@/components/AppLayout';
import Icon from '@/components/ui/AppIcon';

interface WatchlistCreator {
  id: string;
  name: string;
  handle: string;
  category: string;
  followers: string;
  isxScore: number;
  marketValue: string;
  riskScore: number;
  change: number;
  alerts: Alert[];
}

interface Alert {
  type: 'price_up' | 'price_down' | 'risk_increase' | 'performance_boost' | 'audience_change';
  message: string;
  date: string;
  severity: 'info' | 'warning' | 'positive' | 'negative';
}

const alertConfig = {
  price_up: { icon: 'ArrowTrendingUpIcon', color: 'text-positive' },
  price_down: { icon: 'ArrowTrendingDownIcon', color: 'text-negative' },
  risk_increase: { icon: 'ExclamationTriangleIcon', color: 'text-warning' },
  performance_boost: { icon: 'BoltIcon', color: 'text-primary' },
  audience_change: { icon: 'UsersIcon', color: 'text-info' },
};

const severityBg = {
  info: 'bg-info/5 border-info/20',
  warning: 'bg-warning/5 border-warning/20',
  positive: 'bg-positive/5 border-positive/20',
  negative: 'bg-negative/5 border-negative/20',
};

const creators: WatchlistCreator[] = [
  {
    id: '1', name: 'Amel Benali', handle: '@amel.beauty', category: 'Beauty', followers: '284K',
    isxScore: 87, marketValue: '4.2M', riskScore: 12, change: +3.2,
    alerts: [
      { type: 'price_up', message: 'Market Value +3.2% cette semaine', date: 'Aujourd\'hui', severity: 'positive' },
      { type: 'performance_boost', message: 'Engagement rate en hausse : 8.7% → 9.1%', date: 'Hier', severity: 'positive' },
    ]
  },
  {
    id: '2', name: 'Karim Tech', handle: '@karim.tech.dz', category: 'Tech', followers: '156K',
    isxScore: 79, marketValue: '2.8M', riskScore: 18, change: -1.1,
    alerts: [
      { type: 'audience_change', message: 'Variation audience détectée : +2.3K followers en 48h', date: 'Il y a 2j', severity: 'info' },
    ]
  },
  {
    id: '3', name: 'Sara Cuisine', handle: '@sara.cuisine.dz', category: 'Food', followers: '98K',
    isxScore: 72, marketValue: '1.9M', riskScore: 8, change: +1.8,
    alerts: []
  },
  {
    id: '4', name: 'Yacine Sport', handle: '@yacine.sport', category: 'Sport', followers: '210K',
    isxScore: 83, marketValue: '3.4M', riskScore: 22, change: -0.5,
    alerts: [
      { type: 'risk_increase', message: 'Risk Score augmenté : 18 → 22. Vérification en cours.', date: 'Il y a 3j', severity: 'warning' },
    ]
  },
];

const portfolios = [
  { id: 'p1', name: 'Campagne Ramadan 2027', count: 8, totalReach: '1.2M' },
  { id: 'p2', name: 'Beauty Partners Q4', count: 5, totalReach: '840K' },
  { id: 'p3', name: 'Tech Influencers', count: 3, totalReach: '520K' },
];

export default function BrandWatchlistPage() {
  const [activeTab, setActiveTab] = useState<'watchlist' | 'portfolios' | 'alerts'>('watchlist');
  const [selectedPortfolio, setSelectedPortfolio] = useState('p1');

  const allAlerts = creators.flatMap(c => c.alerts.map(a => ({ ...a, creator: c.name })));

  return (
    <AppLayout currentPath="/brand-watchlist">
      <div className="space-y-8 animate-fade-in">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span className="text-xs font-700 uppercase tracking-widest text-primary bg-primary/10 px-2 py-0.5 rounded-full">Brand & Agency</span>
            </div>
            <h1 className="text-2xl font-700 text-foreground">Portfolios & Watchlist</h1>
            <p className="text-sm text-muted-foreground mt-1">Suivi des créateurs · Alertes intelligentes · Portfolios privés</p>
          </div>
          <button className="flex items-center gap-1.5 px-3 py-2 bg-primary hover:bg-primary/90 text-primary-foreground rounded-lg text-sm font-600 transition-colors">
            <Icon name="PlusIcon" size={14} />
            Ajouter un créateur
          </button>
        </div>

        {/* Privacy Notice */}
        <div className="bg-muted/40 border border-border rounded-xl p-3 flex gap-2">
          <Icon name="LockClosedIcon" size={14} className="text-muted-foreground flex-shrink-0 mt-0.5" />
          <p className="text-xs text-muted-foreground">Les portfolios sont privés. Les créateurs ne savent pas dans quel portfolio ils ont été ajoutés, afin de protéger la stratégie commerciale confidentielle de votre organisation.</p>
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-1 bg-muted rounded-xl p-1 w-fit">
          {[{ key: 'watchlist', label: 'Watchlist' }, { key: 'portfolios', label: 'Portfolios' }, { key: 'alerts', label: `Alertes (${allAlerts.length})` }].map(t => (
            <button key={t.key} onClick={() => setActiveTab(t.key as typeof activeTab)} className={`px-4 py-2 rounded-lg text-xs font-600 transition-all ${activeTab === t.key ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}>{t.label}</button>
          ))}
        </div>

        {/* Watchlist */}
        {activeTab === 'watchlist' && (
          <div className="bg-card border border-border rounded-xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border bg-muted/30">
                    <th className="text-left px-4 py-3 text-[11px] font-700 uppercase tracking-wide text-muted-foreground">Créateur</th>
                    <th className="text-left px-4 py-3 text-[11px] font-700 uppercase tracking-wide text-muted-foreground">Catégorie</th>
                    <th className="text-right px-4 py-3 text-[11px] font-700 uppercase tracking-wide text-muted-foreground">ISX Score</th>
                    <th className="text-right px-4 py-3 text-[11px] font-700 uppercase tracking-wide text-muted-foreground">Market Value</th>
                    <th className="text-right px-4 py-3 text-[11px] font-700 uppercase tracking-wide text-muted-foreground">Risk</th>
                    <th className="text-right px-4 py-3 text-[11px] font-700 uppercase tracking-wide text-muted-foreground">7j</th>
                    <th className="text-left px-4 py-3 text-[11px] font-700 uppercase tracking-wide text-muted-foreground">Alertes</th>
                  </tr>
                </thead>
                <tbody>
                  {creators.map((c, i) => (
                    <tr key={c.id} className={`border-b border-border/50 hover:bg-muted/20 transition-colors ${i % 2 === 0 ? '' : 'bg-muted/10'}`}>
                      <td className="px-4 py-3">
                        <p className="text-sm font-600 text-foreground">{c.name}</p>
                        <p className="text-xs text-muted-foreground">{c.handle} · {c.followers}</p>
                      </td>
                      <td className="px-4 py-3"><span className="text-xs bg-muted px-2 py-0.5 rounded-full text-muted-foreground">{c.category}</span></td>
                      <td className="px-4 py-3 text-right">
                        <span className={`font-mono text-sm font-700 ${c.isxScore >= 85 ? 'text-positive' : c.isxScore >= 70 ? 'text-warning' : 'text-negative'}`}>{c.isxScore}</span>
                      </td>
                      <td className="px-4 py-3 text-right font-mono text-xs text-foreground">{c.marketValue}</td>
                      <td className="px-4 py-3 text-right">
                        <span className={`font-mono text-xs font-600 ${c.riskScore <= 15 ? 'text-positive' : c.riskScore <= 25 ? 'text-warning' : 'text-negative'}`}>{c.riskScore}</span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <span className={`text-xs font-600 font-mono ${c.change > 0 ? 'text-positive' : 'text-negative'}`}>{c.change > 0 ? '+' : ''}{c.change}%</span>
                      </td>
                      <td className="px-4 py-3">
                        {c.alerts.length > 0 ? (
                          <span className="inline-flex items-center gap-1 text-[10px] font-700 bg-warning/10 text-warning border border-warning/20 px-2 py-0.5 rounded-full">
                            <Icon name="BellIcon" size={10} />
                            {c.alerts.length}
                          </span>
                        ) : (
                          <span className="text-xs text-muted-foreground">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Portfolios */}
        {activeTab === 'portfolios' && (
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
            <div className="space-y-3">
              <h2 className="text-sm font-700 text-foreground">Mes portfolios</h2>
              {portfolios.map(p => (
                <button key={p.id} onClick={() => setSelectedPortfolio(p.id)} className={`w-full text-left p-4 rounded-xl border transition-all ${selectedPortfolio === p.id ? 'border-primary bg-primary/5' : 'border-border bg-card hover:border-primary/50'}`}>
                  <p className="text-sm font-700 text-foreground">{p.name}</p>
                  <div className="flex items-center gap-3 mt-1">
                    <span className="text-xs text-muted-foreground">{p.count} créateurs</span>
                    <span className="text-xs text-muted-foreground">·</span>
                    <span className="text-xs text-muted-foreground">Reach: {p.totalReach}</span>
                  </div>
                </button>
              ))}
              <button className="w-full p-4 rounded-xl border border-dashed border-border text-sm text-muted-foreground hover:border-primary/50 hover:text-foreground transition-all flex items-center justify-center gap-2">
                <Icon name="PlusIcon" size={14} />
                Nouveau portfolio
              </button>
            </div>
            <div className="xl:col-span-2 bg-card border border-border rounded-xl p-5">
              <h2 className="text-sm font-700 text-foreground mb-4">{portfolios.find(p => p.id === selectedPortfolio)?.name}</h2>
              <div className="space-y-3">
                {creators.slice(0, 3).map(c => (
                  <div key={c.id} className="flex items-center justify-between p-3 bg-muted/30 rounded-xl">
                    <div>
                      <p className="text-sm font-600 text-foreground">{c.name}</p>
                      <p className="text-xs text-muted-foreground">{c.category} · {c.followers}</p>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <p className="text-xs text-muted-foreground">ISX Score</p>
                        <p className={`text-sm font-700 font-mono ${c.isxScore >= 85 ? 'text-positive' : 'text-warning'}`}>{c.isxScore}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-muted-foreground">Risk</p>
                        <p className={`text-sm font-700 font-mono ${c.riskScore <= 15 ? 'text-positive' : 'text-warning'}`}>{c.riskScore}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Alerts */}
        {activeTab === 'alerts' && (
          <div className="space-y-4">
            <div className="bg-muted/40 border border-border rounded-xl p-3 flex gap-2">
              <Icon name="InformationCircleIcon" size={14} className="text-muted-foreground flex-shrink-0 mt-0.5" />
              <p className="text-xs text-muted-foreground">Les alertes ne se déclenchent que pour des variations significatives. Les alertes de fraude importantes sont vérifiées par un humain avant envoi.</p>
            </div>
            {allAlerts.length === 0 ? (
              <div className="bg-card border border-border rounded-xl p-8 text-center">
                <Icon name="BellSlashIcon" size={32} className="text-muted-foreground mx-auto mb-2" />
                <p className="text-sm text-muted-foreground">Aucune alerte active</p>
              </div>
            ) : (
              allAlerts.map((alert, i) => {
                const cfg = alertConfig[alert.type];
                return (
                  <div key={i} className={`flex items-start gap-3 p-4 rounded-xl border ${severityBg[alert.severity]}`}>
                    <Icon name={cfg.icon as Parameters<typeof Icon>[0]['name']} size={16} className={`${cfg.color} flex-shrink-0 mt-0.5`} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <span className="text-sm font-600 text-foreground">{alert.creator}</span>
                        <span className="text-xs text-muted-foreground">{alert.date}</span>
                      </div>
                      <p className="text-xs text-muted-foreground">{alert.message}</p>
                    </div>
                    <button className="text-muted-foreground hover:text-foreground transition-colors flex-shrink-0">
                      <Icon name="XMarkIcon" size={14} />
                    </button>
                  </div>
                );
              })
            )}
          </div>
        )}
      </div>
    </AppLayout>
  );
}
